import { AdminCompany, OnboardingCompanyStatus } from 'src/modules/companies/types';
import { User } from 'src/constants/types';
import apiClient from 'src/api/axiosClient';
import { CompanyPhoneType } from 'src/modules/companies/api/CompanyPhoneApi';
import {
  OnboardingWorkflowType,
  OnboardingStep,
  PhoneNumberType,
  OnboardingStepType,
  OnboardingState,
} from '../types';

const mapCompanyPhoneTypeToPhoneNumberType = (type: CompanyPhoneType): PhoneNumberType => {
  switch (type) {
    case CompanyPhoneType.toll_free:
      return 'toll_free';
    case CompanyPhoneType._10dlc_local:
      return '10dlc_new';
    case CompanyPhoneType._10dlc_hosted:
      return '10dlc_hosted';
    default:
      return '10dlc_hosted';
  }
};

export const hasMarketingUseCase = (usecase?: string | null, subUsecases: string[] = []): boolean => {
  return [usecase, ...subUsecases].some(value => value?.toLowerCase().includes('marketing'));
};

export const formatCompanyMessageFlowText = (companyName: string, useCase?: string | null, subUsecases: string[] = []): string => {
  const optinMessageIncludesMarketing = hasMarketingUseCase(useCase, subUsecases);
  const message = `Welcome to ${companyName}! Reply HELP for Help or STOP to Opt-out anytime. Msg and data rates may apply.${
    optinMessageIncludesMarketing ? ' Marketing messages may be sent.' : ''
  } Msg frequency may vary.`;

  return message;
};

export const determinePhoneNumberType = async (company: AdminCompany): Promise<PhoneNumberType> => {
  const hostedOrders = company.additionalData?.telnyx?.hostedNumberOrders || [];

  if (hostedOrders.length > 0) {
    return '10dlc_hosted';
  }

  try {
    const response = await apiClient.get(`/companies/${company.id}/phone`);
    const phoneNumbers = response.data;

    if (phoneNumbers.length > 0) {
      const firstPhone = phoneNumbers[0];
      return mapCompanyPhoneTypeToPhoneNumberType(firstPhone.type);
    }
  } catch (error) {
    console.error('Error fetching company phone numbers:', error);
  }

  return '10dlc_hosted';
};

export const isUserCompanyAdmin = (user: User): boolean => {
  if (!user.role) {
    return false;
  }
  return user.role.name === 'company_admin';
};

const createStep = (
  type: OnboardingStepType,
  title: string,
  description: string,
  required: boolean,
  order: number,
): OnboardingStep => ({
  type,
  title,
  description,
  status: 'pending',
  required,
  order,
});

const determineStepStatus = (
  stepType: OnboardingStepType,
  company: AdminCompany,
  backendOnboardingStatus?: OnboardingCompanyStatus,
): { status: 'pending' | 'completed' | 'failed'; errorMessage?: string } => {
  const telnyxError = company.additionalData?.telnyx?.error;

  switch (stepType) {
    case 'add_payment':
      if (checkIfStepCompleted('add_payment')) {
        return { status: 'completed' };
      }
      return { status: 'pending' };

    case 'phone_verification':
      if (
        company.additionalData?.telnyx?.hostedNumberOrders?.[0]?.phoneNumbers?.[0]
          ?.verificationStatus == 'verified'
      ) {
        return { status: 'completed' };
      }
      return { status: 'pending' };

    case 'upload_w9':
      if (checkIfStepCompleted('upload_w9')) {
        return { status: 'completed' };
      }
      if (telnyxError) {
        return { status: 'failed', errorMessage: telnyxError };
      }
      return { status: 'pending' };

    case 'upload_phone_bill':
      if (checkIfStepCompleted('upload_phone_bill')) {
        return { status: 'completed' };
      }
      return { status: 'pending' };

    case 'sign_loa':
      if (checkIfStepCompleted('sign_loa')) {
        return { status: 'completed' };
      }
      return { status: 'pending' };

    case 'brand_info': {
      if (checkIfStepCompleted('brand_info')) {
        return { status: 'completed' };
      }
      return { status: 'pending' };
    }

    case 'invite_users':
      if (checkIfStepCompleted(stepType)) {
        return { status: 'completed' };
      }
      return { status: 'pending' };

    default:
      return { status: 'pending' };
  }

  function checkIfStepCompleted(stepType: OnboardingStepType) {
    return backendOnboardingStatus?.steps.find(s => s.step === stepType)?.completed;
  }
};

const buildTollFreeWorkflow = (
  company: AdminCompany,
): OnboardingStep[] => {
  const steps: OnboardingStep[] = [
    createStep(
      'add_payment',
      'Add Payment Method',
      'Add your payment information to activate your subscription',
      true,
      1,
    ),
  ];

  return steps.map(step => {
    const { status, errorMessage } = determineStepStatus(
      step.type,
      company,
    );
    return { ...step, status, errorMessage };
  });
};

const build10DLCNewWorkflow = (
  company: AdminCompany,
): OnboardingStep[] => {
  const steps: OnboardingStep[] = [
    createStep(
      'add_payment',
      'Add Payment Method',
      'Add your payment information to activate your subscription',
      true,
      1,
    ),
    createStep('upload_w9', 'Upload W9', 'Upload your W9 form for brand registration', true, 2),
    createStep('brand_info', 'Organization Details', 'Confirm your organization information', true, 3),
  ];

  return steps.map(step => {
    const { status, errorMessage } = determineStepStatus(
      step.type,
      company,
    );
    return { ...step, status, errorMessage };
  });
};

const build10DLCHostedWorkflow = (
  company: AdminCompany,
): OnboardingStep[] => {
  const steps: OnboardingStep[] = [
    createStep(
      'add_payment',
      'Add Payment Method',
      'Add your payment information to activate your subscription',
      true,
      1,
    ),
    createStep('upload_w9', 'Upload W9', 'Upload your W9 form for brand registration', true, 2),
    createStep('brand_info', 'Organization Details', 'Confirm your organization information', true, 3),
    createStep(
      'upload_phone_bill',
      'Upload Phone Bill',
      'Upload a copy of your phone bill',
      true,
      4,
    ),
    createStep(
      'sign_loa',
      'Sign Letter of Authorization',
      'Sign the LOA to authorize the phone number transfer',
      true,
      5,
    ),
    createStep(
      'phone_verification',
      'Verify phone number',
      `One last step before your number can send and receive messages via ${company.application.name ?? 'AgencyText'}.`,
      true,
      6,
    ),
  ];

  return steps.map(step => {
    const { status, errorMessage } = determineStepStatus(
      step.type,
      company,
    );
    return { ...step, status, errorMessage };
  });
};

export const buildOnboardingWorkflow = (
  company: AdminCompany,
  phoneNumberType: PhoneNumberType,
  backendOnboardingStatus?: OnboardingCompanyStatus,
): OnboardingWorkflowType => {
  let steps: OnboardingStep[];

  switch (phoneNumberType) {
    case 'toll_free':
      steps = buildTollFreeWorkflow(company);
      break;
    case '10dlc_new':
      steps = build10DLCNewWorkflow(company);
      break;
    case '10dlc_hosted':
      steps = build10DLCHostedWorkflow(company);
      break;
    default:
      steps = [];
  }

  // Override step statuses with backend data if available
  if (backendOnboardingStatus) {
    steps = steps.map(step => {
      const backendStep = backendOnboardingStatus.steps.find(s => s.step === step.type);
      if (backendStep) {
        return {
          ...step,
          status: backendStep.completed ? 'completed' : 'pending',
          required: backendStep.required,
          completedManually: backendStep.completedManually,
        };
      }
      return step;
    });
  }

  const hasFailedSteps = steps.some(s => s.status === 'failed');
  const hasOutstandingTasks = steps.some(
    s => s.required && (s.status === 'pending' || s.status === 'failed'),
  );
  const isComplete = steps.every(s => !s.required || s.status === 'completed');

  return {
    phoneNumberType,
    steps,
    isComplete,
    hasFailedSteps,
    hasOutstandingTasks,
  };
};

export const determineOnboardingState = async (
  company: AdminCompany | undefined,
  profile: User,
  onboardingStatus?: OnboardingCompanyStatus,
): Promise<(OnboardingState & { company: AdminCompany }) | null> => {
  if (!company) {
    return null;
  }

  const isOnboarding = onboardingStatus?.onboardingStatus !== 'completed';

  const isCompanyAdmin = isUserCompanyAdmin(profile);

  if (!isOnboarding && !isCompanyAdmin) {
    return null;
  }

  const phoneNumberType = await determinePhoneNumberType(company);

  const workflow = buildOnboardingWorkflow(company, phoneNumberType, onboardingStatus);

  if (workflow.isComplete) {
    return {
      isOnboarding: false,
      isCompanyAdmin: isUserCompanyAdmin(profile),
      workflow,
      currentStepIndex: workflow.steps.length - 1,
      company,
    };
  }

  const currentStepIndex = workflow.steps.findIndex(
    s => s.status === 'pending' || s.status === 'failed',
  );

  return {
    isOnboarding: true,
    isCompanyAdmin: isUserCompanyAdmin(profile),
    workflow,
    currentStepIndex: currentStepIndex >= 0 ? currentStepIndex : workflow.steps.length - 1,
    company,
  };
};

export const shouldShowOnboardingFlow = (onboardingState: OnboardingState | null): boolean => {
  if (!onboardingState || !onboardingState.isOnboarding) {
    return false;
  }

  if (!onboardingState.workflow) {
    return false;
  }

  // If workflow is complete, don't show onboarding
  if (onboardingState.workflow.isComplete) {
    return false;
  }

  // Otherwise, show onboarding if there are completed steps or failed steps
  return onboardingState.workflow.steps.some(s => s.status === 'completed') || onboardingState.workflow.hasFailedSteps;
};
